import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Package, MapPin, DollarSign, Weight, Calendar, Calculator, Upload, Zap } from 'lucide-react';
import { toast } from 'sonner';
import RateCalculator from '@/components/RateCalculator';
import CSVImporter from '@/components/CSVImporter';
import QuickAssignModal from '@/components/QuickAssignModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoadsPage = () => {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [quickAssignOpen, setQuickAssignOpen] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [formData, setFormData] = useState({
    pickup_address: '',
    pickup_lat: '',
    pickup_lng: '',
    delivery_address: '',
    delivery_lat: '',
    delivery_lng: '',
    weight: '',
    distance_miles: '',
    rate: '',
    commodity: '',
    shipper_name: '',
    consignee_name: '',
    pickup_date: '',
    delivery_date: '',
  });

  useEffect(() => {
    fetchLoads();
  }, []);

  const fetchLoads = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/loads`);
      setLoads(response.data);
    } catch (error) {
      console.error('Error fetching loads:', error);
      toast.error('Failed to fetch loads');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        pickup_address: formData.pickup_address,
        pickup_location: {
          type: 'Point',
          coordinates: [parseFloat(formData.pickup_lng), parseFloat(formData.pickup_lat)]
        },
        delivery_address: formData.delivery_address,
        delivery_location: {
          type: 'Point',
          coordinates: [parseFloat(formData.delivery_lng), parseFloat(formData.delivery_lat)]
        },
        weight: parseFloat(formData.weight),
        distance_miles: parseFloat(formData.distance_miles),
        rate: parseFloat(formData.rate),
        commodity: formData.commodity,
        shipper_name: formData.shipper_name,
        consignee_name: formData.consignee_name,
        pickup_date: formData.pickup_date,
        delivery_date: formData.delivery_date,
      };

      await axios.post(`${API}/loads`, payload);
      toast.success('Load created successfully');
      setOpen(false);
      fetchLoads();
      setFormData({
        pickup_address: '',
        pickup_lat: '',
        pickup_lng: '',
        delivery_address: '',
        delivery_lat: '',
        delivery_lng: '',
        weight: '',
        distance_miles: '',
        rate: '',
        commodity: '',
        shipper_name: '',
        consignee_name: '',
        pickup_date: '',
        delivery_date: '',
      });
    } catch (error) {
      console.error('Error creating load:', error);
      toast.error('Failed to create load');
    }
  };

  const updateStatus = async (loadId, status) => {
    try {
      await axios.put(`${API}/loads/${loadId}/status?status=${status}`);
      toast.success('Status updated');
      fetchLoads();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const openQuickAssign = (load) => {
    setSelectedLoad(load);
    setQuickAssignOpen(true);
  };

  const handleAssigned = (result) => {
    toast.success(`Assigned to ${result.driver_name}`);
    fetchLoads();
  };

  const LoadCard = ({ load }) => (
    <Card className="glass-card hover:border-primary/30 transition-all duration-300" data-testid={`load-card-${load.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/15">
              <Package className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-mono text-muted-foreground">LOAD #{load.id.substring(0, 8)}</p>
              <p className="text-sm font-semibold text-foreground mt-1">{load.commodity}</p>
            </div>
          </div>
          <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-md ${
            load.status === 'completed' ? 'bg-emerald-500/15 text-emerald-500' :
            load.status === 'in_transit' ? 'bg-amber-500/15 text-amber-500' :
            load.status === 'assigned' ? 'bg-blue-500/15 text-blue-500' :
            'bg-red-500/15 text-red-500'
          }`}>
            {load.status}
          </span>
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-primary mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Pickup</p>
              <p className="text-sm text-foreground">{load.pickup_address}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-emerald-500 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Delivery</p>
              <p className="text-sm text-foreground">{load.delivery_address}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border/50">
          <div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Weight className="w-3 h-3" /> Weight
            </p>
            <p className="text-sm font-mono font-semibold text-foreground">{load.weight} lbs</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Rate
            </p>
            <p className="text-sm font-mono font-semibold text-foreground">${load.rate}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Distance</p>
            <p className="text-sm font-mono font-semibold text-foreground">{load.distance_miles} mi</p>
          </div>
        </div>

        {load.status === 'pending' && (
          <div className="flex gap-2 mt-4">
            <Button
              onClick={() => openQuickAssign(load)}
              className="flex-1 glow-button"
              data-testid={`quick-assign-${load.id}`}
            >
              <Zap className="w-4 h-4 mr-2" />
              Quick Assign
            </Button>
          </div>
        )}
        {load.status === 'assigned' && load.assigned_driver_name && (
          <div className="mt-4 pt-4 border-t border-border/50">
            <p className="text-xs text-muted-foreground">Assigned to:</p>
            <p className="text-sm font-semibold text-foreground">{load.assigned_driver_name}</p>
            {load.assigned_driver_phone && (
              <p className="text-xs text-muted-foreground">{load.assigned_driver_phone}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="p-6 space-y-6 fade-in" data-testid="loads-page-container">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black font-heading tracking-tight text-foreground">Loads Management</h1>
            <p className="text-muted-foreground mt-2">Create, import, and manage freight loads</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="glow-button" data-testid="create-load-dialog-trigger">
                <Plus className="w-4 h-4 mr-2" />
                Create Load
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto glass-card">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading">Create New Load</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="create-load-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Pickup Address</Label>
                    <Input
                      value={formData.pickup_address}
                      onChange={(e) => setFormData({...formData, pickup_address: e.target.value})}
                      required
                      data-testid="input-pickup-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Pickup Lat</Label>
                      <Input
                        type="number"
                        step="any"
                        placeholder="40.7128"
                        value={formData.pickup_lat}
                        onChange={(e) => setFormData({...formData, pickup_lat: e.target.value})}
                        required
                        data-testid="input-pickup-lat"
                      />
                    </div>
                    <div>
                      <Label>Pickup Lng</Label>
                      <Input
                        type="number"
                        step="any"
                        placeholder="-74.0060"
                        value={formData.pickup_lng}
                        onChange={(e) => setFormData({...formData, pickup_lng: e.target.value})}
                        required
                        data-testid="input-pickup-lng"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Delivery Address</Label>
                    <Input
                      value={formData.delivery_address}
                      onChange={(e) => setFormData({...formData, delivery_address: e.target.value})}
                      required
                      data-testid="input-delivery-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Delivery Lat</Label>
                      <Input
                        type="number"
                        step="any"
                        placeholder="34.0522"
                        value={formData.delivery_lat}
                        onChange={(e) => setFormData({...formData, delivery_lat: e.target.value})}
                        required
                        data-testid="input-delivery-lat"
                      />
                    </div>
                    <div>
                      <Label>Delivery Lng</Label>
                      <Input
                        type="number"
                        step="any"
                        placeholder="-118.2437"
                        value={formData.delivery_lng}
                        onChange={(e) => setFormData({...formData, delivery_lng: e.target.value})}
                        required
                        data-testid="input-delivery-lng"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Weight (lbs)</Label>
                    <Input
                      type="number"
                      value={formData.weight}
                      onChange={(e) => setFormData({...formData, weight: e.target.value})}
                      required
                      data-testid="input-weight"
                    />
                  </div>
                  <div>
                    <Label>Distance (miles)</Label>
                    <Input
                      type="number"
                      value={formData.distance_miles}
                      onChange={(e) => setFormData({...formData, distance_miles: e.target.value})}
                      required
                      data-testid="input-distance"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Rate ($)</Label>
                    <Input
                      type="number"
                      value={formData.rate}
                      onChange={(e) => setFormData({...formData, rate: e.target.value})}
                      required
                      data-testid="input-rate"
                    />
                  </div>
                  <div>
                    <Label>Commodity</Label>
                    <Input
                      value={formData.commodity}
                      onChange={(e) => setFormData({...formData, commodity: e.target.value})}
                      required
                      data-testid="input-commodity"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Shipper Name</Label>
                    <Input
                      value={formData.shipper_name}
                      onChange={(e) => setFormData({...formData, shipper_name: e.target.value})}
                      required
                      data-testid="input-shipper"
                    />
                  </div>
                  <div>
                    <Label>Consignee Name</Label>
                    <Input
                      value={formData.consignee_name}
                      onChange={(e) => setFormData({...formData, consignee_name: e.target.value})}
                      required
                      data-testid="input-consignee"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Pickup Date</Label>
                    <Input
                      type="date"
                      value={formData.pickup_date}
                      onChange={(e) => setFormData({...formData, pickup_date: e.target.value})}
                      required
                      data-testid="input-pickup-date"
                    />
                  </div>
                  <div>
                    <Label>Delivery Date</Label>
                    <Input
                      type="date"
                      value={formData.delivery_date}
                      onChange={(e) => setFormData({...formData, delivery_date: e.target.value})}
                      required
                      data-testid="input-delivery-date"
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full glow-button" data-testid="submit-load-button">
                  Create Load
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="loads" className="space-y-6">
          <TabsList className="glass">
            <TabsTrigger value="loads" data-testid="tab-loads">
              <Package className="w-4 h-4 mr-2" />
              All Loads ({loads.length})
            </TabsTrigger>
            <TabsTrigger value="calculator" data-testid="tab-calculator">
              <Calculator className="w-4 h-4 mr-2" />
              Rate Calculator
            </TabsTrigger>
            <TabsTrigger value="import" data-testid="tab-import">
              <Upload className="w-4 h-4 mr-2" />
              Bulk Import
            </TabsTrigger>
          </TabsList>

          <TabsContent value="loads" className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading loads...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loads.map((load) => (
                  <LoadCard key={load.id} load={load} />
                ))}
              </div>
            )}

            {!loading && loads.length === 0 && (
              <Card className="glass-card" data-testid="no-loads-message">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-heading text-foreground mb-2">No Loads Yet</h3>
                  <p className="text-sm text-muted-foreground mb-6">Create your first load to get started</p>
                  <Button onClick={() => setOpen(true)} className="glow-button" data-testid="create-first-load-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Load
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="calculator">
            <RateCalculator />
          </TabsContent>

          <TabsContent value="import">
            <CSVImporter onImportComplete={fetchLoads} />
          </TabsContent>
        </Tabs>

        {/* Quick Assign Modal */}
        <QuickAssignModal
          open={quickAssignOpen}
          onClose={() => setQuickAssignOpen(false)}
          load={selectedLoad}
          onAssigned={handleAssigned}
        />
      </div>
    </Layout>
  );
};

export default LoadsPage;